"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { useParams } from "next/navigation";
import { usePathname, useRouter } from "@/i18n/routing";
import { isEnglishLocale } from "@/lib/market";

type LocaleCode = "nl" | "de" | "en" | "en-GB";

const LOCALES: Array<{ code: LocaleCode; native: string; label: string }> = [
  { code: "nl", native: "Nederlands", label: "NL" },
  { code: "de", native: "Deutsch", label: "DE" },
];

function FlagNL({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 18" xmlns="http://www.w3.org/2000/svg">
      <rect fill="#AE1C28" height="6" width="24" y="0" />
      <rect fill="#FFFFFF" height="6" width="24" y="6" />
      <rect fill="#21468B" height="6" width="24" y="12" />
    </svg>
  );
}

function FlagDE({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 18" xmlns="http://www.w3.org/2000/svg">
      <rect fill="#000000" height="6" width="24" y="0" />
      <rect fill="#DD0000" height="6" width="24" y="6" />
      <rect fill="#FFCE00" height="6" width="24" y="12" />
    </svg>
  );
}

function Flag({ code, className }: { code: LocaleCode; className?: string }) {
  return code === "nl" ? <FlagNL className={className} /> : <FlagDE className={className} />;
}

const HIDDEN_PATH_PREFIXES = ["/style/", "/orientation/", "/theme/", "/preview/", "/checkout/", "/order/"];

type LanguageSwitcherProps = {
  // "floating" is the desktop bottom-left pill (hidden on mobile); "inline" is
  // the in-footer variant shown only on mobile, so it never overlays the sticky
  // buy bar at the bottom of the screen.
  variant?: "floating" | "inline";
};

export function LanguageSwitcher({ variant = "floating" }: LanguageSwitcherProps) {
  const locale = useLocale() as LocaleCode;
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // On /en (US market) and /uk (UK market) we don't show the switcher — prices differ across
  // locales so surfacing other locales would be misleading.
  const hidden = isEnglishLocale(locale) || HIDDEN_PATH_PREFIXES.some((p) => pathname === p.slice(0, -1) || pathname.startsWith(p));

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const switchTo = (next: LocaleCode) => {
    setOpen(false);
    if (next === locale) return;
    // next-intl's typed router can't statically resolve params for dynamic-route pathnames
    // when called generically (the union includes /[id] routes). The object form with
    // `params` is the documented way to swap locales while preserving the current route.
    router.replace(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { pathname, params } as any,
      { locale: next },
    );
  };

  if (hidden) return null;

  const isFloating = variant === "floating";

  return (
    <div
      className={
        isFloating
          ? "fixed bottom-4 left-4 z-50 hidden md:bottom-6 md:left-6 md:block"
          : "relative md:hidden"
      }
      ref={ref}
      style={isFloating ? { paddingBottom: "env(safe-area-inset-bottom)" } : undefined}
    >
      {open ? (
        <div className="absolute bottom-full left-0 mb-2 w-44 border border-rule bg-paper">
          <p className="border-b border-rule px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-ink-faint">
            Language
          </p>
          <ul role="listbox">
            {LOCALES.map((l) => {
              const active = l.code === locale;
              return (
                <li key={l.code}>
                  <button
                    aria-selected={active}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-paper-deep ${
                      active ? "text-accent" : "text-ink"
                    }`}
                    onClick={() => switchTo(l.code)}
                    role="option"
                    type="button"
                  >
                    <span className="flex items-center gap-2.5">
                      <Flag className="h-3.5 w-[18px] border border-rule" code={l.code} />
                      <span className="font-sans text-sm">{l.native}</span>
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-faint">
                      {l.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        // The accessible name must contain the visible "NL"/"DE" text
        // (label-content-name-mismatch), so lead with it.
        aria-label={`${locale.toUpperCase()} — ${LOCALES.find((l) => l.code === locale)?.native ?? locale}`}
        className="flex h-10 items-center gap-2 border border-rule bg-paper px-2.5 text-ink transition-colors hover:bg-paper-deep md:h-11 md:px-3"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <Flag className="h-4 w-[22px] border border-rule" code={locale} />
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-soft">
          {locale.toUpperCase()}
        </span>
      </button>
    </div>
  );
}
